import { derived, writable, type Readable } from 'svelte/store';

// ThemeChoice is the member's choice, kept per browser (spec 010).
export type ThemeChoice = 'system' | 'light' | 'dark';

// Theme is what shows: the choice, or the device's mode under System.
export type Theme = 'light' | 'dark';

// STORAGE_KEY is shared with the pre-paint script in index.html.
const STORAGE_KEY = 'kehai.theme';

function readChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'light' || v === 'dark') return v;
  } catch {
    // Storage can be off in private windows; System applies.
  }
  return 'system';
}

const media = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;
const systemDark = writable(media?.matches ?? false);
media?.addEventListener('change', (e) => systemDark.set(e.matches));

export const themeChoice = writable<ThemeChoice>(readChoice());

// theme is the resolved theme; a device that cannot report its mode reads
// as light.
export const theme: Readable<Theme> = derived([themeChoice, systemDark], ([c, d]) => (c === 'system' ? (d ? 'dark' : 'light') : c));

// setThemeChoice records the member's choice for this browser.
export function setThemeChoice(c: ThemeChoice) {
  try {
    if (c === 'system') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, c);
  } catch {
    // Not kept past this page when storage is off.
  }
  themeChoice.set(c);
}

theme.subscribe((t) => {
  if (typeof document !== 'undefined') document.documentElement.dataset.theme = t;
});
